import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { C3Service } from './c3.service';
import * as cee from './Common/CeeEnvisionWebComponents';
import { timer, BehaviorSubject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { AnimationService } from './animation.service';

export interface Model {
  key: string;
  name: string;
}

// can be removed
export interface TaskPool {
  id: number;
  alive: boolean;
}

export enum StreamingStatus {
  LOADING_GEOMETRY = 'streaming geometry',
  ISO_VOLUME = 'streaming iso volume',
  ISO_RANGES = 'setting iso ranges',
}

// shift to interfaces
export interface ProgressTask {
  id: any;
  progress: number;
  alive: boolean;
  streamingStatus: StreamingStatus;
  streamingState: number;
}

@Injectable()
export class ModelService implements OnDestroy {
  private _modelOpen = false;
  private _animationBarOpen = false;
  private openAnimationBar = false;
  private _modelName = '';
  private _c3Service: C3Service;
  private _scalarData: string[] = [];
  private _vectorData: string[] = [];

  public progress: ProgressTask[] = [];
  private callbacks: cee.ug.ProgressData[] = [];

  public modelOpened = new BehaviorSubject<boolean>(false);
  public modelListShow = new EventEmitter();

  public models: ReadonlyArray<Model> = [];
  public taskPool: TaskPool[] = [];
  public scalarResult = new BehaviorSubject(false);

  public get modelOpen() {
    return this._modelOpen;
  }
  public get modelName() {
    return this._modelName;
  }
  public get animationBarOpen() {
    return this._animationBarOpen;
  }

  public get scalarData() {
    return this._scalarData;
  }

  public get vectorData() {
    return this._vectorData;
  }

  public constructor(c3Service: C3Service) {
    this._c3Service = c3Service;
  }

  public openModel(key: string, id?: string, animationService?: AnimationService, openAnimationBar: boolean = false, result?: string) {
    this.openAnimationBar = openAnimationBar;

    if (key) {
      this._modelOpen = false;
      const modelIdStr = id || '0';

      const modelId = parseFloat(modelIdStr);

      // can be removed
      this.taskPool.push({ id: modelId, alive: true });
      this.progress.push({
        id: modelId,
        progress: 0,
        alive: true,
        streamingStatus: StreamingStatus.LOADING_GEOMETRY,
        streamingState: 0,
      });

      this._c3Service._model[modelIdStr].openModel(key, (model?: cee.ug.OpenModelCallback) => {
        this._c3Service.initModel(modelIdStr);
        this.modelOpened.next(true);
        this._modelOpen = true;

        //this.callProgressHandler(modelId, animationService);

        animationService !== undefined ? this.callProgressHandler(modelId, animationService, result) : this.callProgressHandler(modelId);
      });
    }
  }

  public openMapperModel(key?: any) {
    if (key) {
      this._modelOpen = false;
      const modelId = key['modelKey'];
      const type = key['type'];

      this.progress.push({
        id: modelId,
        progress: 0,
        alive: true,
        streamingStatus: StreamingStatus.LOADING_GEOMETRY,
        streamingState: 0,
      });

      this._c3Service._model[modelId].openModel(key, (model?: cee.ug.OpenModelCallback) => {
        this._c3Service.initMapperModel(modelId, type);
        this.modelOpened.next(true);
        this._modelOpen = true;

        this.callProgressHandler(modelId);
      });
    }
  }

  public openSinterModel(key: string, id?: string, animationService?: AnimationService, openAnimationBar: boolean = false) {
    this.openAnimationBar = openAnimationBar;

    if (key) {
      this._modelOpen = false;
      const modelIdStr = id || '0';

      const modelId = parseFloat(modelIdStr);

      this.taskPool.push({ id: modelId, alive: true });
      this.progress.push({
        id: modelId,
        progress: 0,
        alive: true,
        streamingStatus: StreamingStatus.LOADING_GEOMETRY,
        streamingState: 0,
      });

      this._c3Service._model[modelId].openModel(key, (model?: cee.ug.OpenModelCallback) => {
        this._c3Service.initModel(modelIdStr);
        this.modelOpened.next(true);
        this._modelOpen = true;

        //this.callProgressHandler(modelId, animationService);

        // animationService !== undefined ? this.callProgressHandler(modelId, animationService) : this.callProgressHandler(modelId);
        this.setIsoVolume(modelId, animationService);
      });
    }
  }

  private callProgressHandler(modelId: number, animationService?: AnimationService, result?: string) {
    const updateProgressCallback = (progressData: cee.ug.ProgressData) => {
      const currenProgressTask: ProgressTask = this.progress.filter((x) => x.id == modelId)[0];

      if (progressData.streamingStatus === 1 || progressData.streamingStatus === 2) {
        currenProgressTask.progress = progressData.pctDone;
        currenProgressTask.alive = progressData.pctDone === 100 ? false : true;

        if (animationService) {
          this.handleStreamingStatus(currenProgressTask, animationService, result);
        }
      }
    };

    this.callbacks.push(updateProgressCallback.prototype);
    this._c3Service._model[modelId.toString()].setProgressHandler(updateProgressCallback);
  }

  private handleStreamingStatus(task: ProgressTask, animationService?: AnimationService, result?: string) {
    task.streamingState = task.progress === 100 ? task.streamingState : task.streamingState + 1;

    switch (task.streamingState) {
      case 0:
        task.streamingStatus = StreamingStatus.LOADING_GEOMETRY;
        break;
      case 1:
        task.streamingStatus = StreamingStatus.ISO_VOLUME;
        break;
      case 2:
        task.streamingStatus = StreamingStatus.ISO_RANGES;
        break;
    }

    if (task.streamingStatus === StreamingStatus.ISO_VOLUME) {
      animationService ? this.setIsoVolume(task.id, animationService, task, result) : this.setIsoVolume(task.id);
    }
  }

  public clearTask() {
    this._modelOpen = false;
    this.taskPool = [];
    this.progress = [];
    this.callbacks = [];
  }

  public showModelList() {
    this.modelListShow.next(null);
  }

  public setSMapScalar(name: string, id = 0) {
    const isoObject = this._c3Service._model[id].getIsovolumeAt(0);
    isoObject.mapScalarResultId = this.findResultid(name, id);
  }

  public setSMapScalarMulti(name: string) {
    this.taskPool.forEach((item) => {
      const id = item.id;
      const isoObject = this._c3Service._model[id].getIsovolumeAt(0);
      isoObject.mapScalarResultId = this.findResultid(name, 0);
    });
  }

  public setIsoScalar(name: string, id = 0) {
    const isoObject = this._c3Service._model[id].getIsovolumeAt(id);
    isoObject.isoScalarResultId = this.findResultid(name, id);
  }

  public setVectorScalar(name: string, id = 0) {
    const modelSpec = this._c3Service._model[id].modelSpec;
    if (name === 'None') {
      modelSpec.vectorResultIdArray = [];
      return;
    }

    modelSpec.vectorResultIdArray = [this.findVectorId(name, id)];
  }

  public setMinIso(value: number) {
    //this.isoObject.minimumIsoValue = value;
  }

  public setMaxIso(value: number) {
    //this.isoObject.maximumIsoValue = value;
  }

  private findResultid(resultname: string, modelId?: number) {
    const modelDir = this._c3Service._model[modelId || 0].modelDirectory;
    for (let i = 0; i < modelDir.scalarResultArray.length; ++i) {
      const name = modelDir.scalarResultArray[i].name;
      if (name.startsWith(resultname)) {
        return modelDir.scalarResultArray[i].id;
      }
    }
    return 0;
  }

  private findVectorId(vector: string, modelId?: number) {
    const modelDir = this._c3Service._model[modelId || 0].modelDirectory;

    for (let i = 0; i < modelDir.vectorResultArray.length; ++i) {
      const name = modelDir.vectorResultArray[i].name;

      if (name.startsWith(vector)) {
        return modelDir.vectorResultArray[i].id;
      }
    }
    return 0;
  }

  public modelDisplacement(checked: boolean, modelId: number) {
    const modelSpec = this._c3Service._model[modelId].modelSpec;

    if (this._c3Service.displacementResults.length > 0) {
      const displacementResultId = this._c3Service.displacementResults[0].id;

      if (checked) {
        modelSpec.displacementResultId = displacementResultId;
      } else if (displacementResultId === modelSpec.displacementResultId) {
        modelSpec.displacementResultId = -1;
      }
    }
  }

  private isNodeScalar(scalar: ReadonlyArray<cee.ug.ResultInfo>) {
    scalar.forEach((item: { name: string }) => {
      if (item.name === 'Density [node]') {
        this.scalarResult.next(true);
      }
    });
    this.scalarResult.next(false);
  }

  public setIsoRanges(modelId: number, isoObject: cee.ug.Isovolume, animationService?: AnimationService) {
    const model = this._c3Service._model[modelId];
    if (model.getScalarResultMaximumValue(isoObject.isoScalarResultId) != undefined && model.getScalarResultMinimumValue(isoObject.isoScalarResultId) != undefined) {
      isoObject.minimumIsoValue = (model.getScalarResultMinimumValue(isoObject.isoScalarResultId) + model.getScalarResultMaximumValue(isoObject.isoScalarResultId)) / 2;
      isoObject.maximumIsoValue = model.getScalarResultMaximumValue(isoObject.isoScalarResultId);

      if (this._c3Service.scalarResults) {
        this.isNodeScalar(this._c3Service.scalarResults);
      }

      this.taskPool.filter((task) => {
        if (task.id === modelId) {
          if (animationService) {
            animationService.skipLast(modelId);
          }
          task.alive = false;

          if (this.openAnimationBar) {
            this._animationBarOpen = true;
          }
        }
      });
    }
  }

  public setIsoVolume(modelId: number, animationService?: AnimationService, task?: ProgressTask, result = 'Density') {
    const modelDir = this._c3Service._model[modelId].modelDirectory;
    const model = this._c3Service._model[modelId];

    if (modelDir.scalarResultArray.length > 0) {
      const resultid = this.findResultid('alpha.Polymer', modelId);
      const alphaid = this.findResultid(result, modelId);
      const isoObject = model.addIsovolume() as cee.ug.Isovolume;

      this.setIsoRanges(modelId, isoObject, animationService);

      isoObject.isoScalarResultId = resultid;

      if (modelDir.scalarResultArray.length > 1) {
        isoObject.mapScalarResultId = alphaid;
      }

      this._c3Service._model[modelId].getPartSettingsArray().forEach((ps: cee.ug.PartSettings) => {
        const greyValue = 222 / 255;
        ps.color = { r: greyValue, g: greyValue, b: greyValue };

        const partName = this._c3Service._model[modelId].modelDirectory.getPartNameById(ps.geometryIndex, ps.partId);
        if (partName.indexOf('procBoundary') >= 0) {
          ps.visible = false;
        }
      });

      isoObject.opacity = 1.0;

      const scalarResSettings = model.getScalarSettingsById(resultid);
      scalarResSettings.setAutoRange(cee.ug.AutoRangeMode.VISIBLE_ITEMS);

      model.setDrawStyleAllParts(cee.ug.DrawStyle.SURFACE, 0.3);

      timer(0, 100)
        .pipe(takeWhile(() => this.taskPool[modelId].alive))
        .subscribe(() => {
          if (model.getScalarResultMinimumValue(isoObject.isoScalarResultId) || model.getScalarResultMaximumValue(isoObject.isoScalarResultId)) {
            if (task !== undefined) {
              task.streamingStatus = StreamingStatus.ISO_RANGES;
              this.setIsoRanges(modelId, isoObject, animationService);
            }
          }
        });
    } else {
      model.setDrawStyleAllParts(cee.ug.DrawStyle.SURFACE, 0.3);
    }
  }

  public setIsoSurface() {
    const modelDir = this._c3Service.model.modelDirectory;
    const model = this._c3Service.model;

    if (modelDir.scalarResultArray.length > 0) {
      const iso = model.addIsosurface();
      iso.isoScalarResultId = modelDir.scalarResultArray[1].id;

      if (model.getScalarResultMinimumValue(iso.isoScalarResultId) != undefined) {
        iso.isoValue = (model.getScalarResultMinimumValue(iso.isoScalarResultId) + model.getScalarResultMaximumValue(iso.isoScalarResultId)) / 2;
      } else {
        iso.isoValue = 0.5;
      }

      if (modelDir.scalarResultArray.length > 1) {
        iso.mapScalarResultId = modelDir.scalarResultArray[3].id;
      } else {
        iso.mapScalarResultId = modelDir.scalarResultArray[3].id;
      }

      iso.opacity = 0.5;
      model.setDrawStyleAllParts(cee.ug.DrawStyle.SURFACE, 0.5);
    } else {
      model.setDrawStyleAllParts(cee.ug.DrawStyle.SURFACE_MESH, 0.5);
    }
  }

  public setDrawStyle(viewer: string) {
    const model = this._c3Service._model[viewer];
    model.setDrawStyleAllParts(cee.ug.DrawStyle.SURFACE_MESH, 1);
  }

  // never called ???
  ngOnDestroy() {
    this.taskPool.forEach((task) => {
      task.alive = false;
    });
  }
}
